import { ProductClass, ProductLevel, ServicesClass } from "../types/enums";
import { worldGroupDatum } from "./queries/getLocationsMetadata";
import { getNumericIdFromStringLocationId } from "../utils/locationUtils";
import { UIView } from "../types/vizEnums";
import {
  PLACEHOLDER_QUERY,
  CPY_QUERY,
  CCPY_QUERY,
  GGPY_QUERY,
  CGPY_QUERY,
  GCPY_QUERY,
  CCY_QUERY,
  CCPY2_QUERY,
  CGPY2_QUERY,
  GCPY2_QUERY,
} from "./queries/exploreDataQueries";

export interface EndpointFacetInputVariableProperties {
  countryId: number | undefined;
  partnerCountryId: number | undefined;
  groupId: number | undefined;
  partnerGroupId: number | undefined;
  productId: number | undefined;
  productLevel: ProductLevel | number | undefined;
}

/**
 * Combined query variables type covering all explore-data endpoint fields.
 * This is the superset of fields accepted by CPY, CCPY, GGPY, CGPY, GCPY,
 * CCY, CCPY2, CGPY2, and GCPY2 queries. Using this avoids `as any` casts
 * when passing variables to useQuery with a dynamically selected query.
 */
export interface ExploreDataQueryVariables extends EndpointFacetInputVariableProperties {
  productClass: ProductClass | undefined;
  servicesClass: ServicesClass;
  yearMin: number | undefined;
  yearMax: number | undefined;
}

export interface EndpointFacetResult {
  queryToUse: typeof PLACEHOLDER_QUERY | typeof CPY_QUERY | typeof CCPY_QUERY | typeof GGPY_QUERY | typeof CGPY_QUERY | typeof GCPY_QUERY | typeof CCY_QUERY | typeof CCPY2_QUERY | typeof CGPY2_QUERY | typeof GCPY2_QUERY;
  queryIsInvalid: boolean;
}

interface QueryParamsInput {
  view: UIView;
  variables: ExploreDataQueryVariables | EndpointFacetInputVariableProperties | null;
}

const numericWorldGroupId = getNumericIdFromStringLocationId(
  worldGroupDatum.groupId,
);

const determineEndpointFacet = ({ view, variables }: QueryParamsInput): EndpointFacetResult => {
  // Handle null/undefined variables (e.g., when year range hasn't loaded yet)
  if (!variables) {
    return { queryToUse: PLACEHOLDER_QUERY, queryIsInvalid: true };
  }

  let {
    countryId,
    partnerCountryId,
    groupId,
    partnerGroupId,
    productId,
  } = variables;

  let queryToUse: typeof PLACEHOLDER_QUERY | typeof CPY_QUERY | typeof CCPY_QUERY | typeof GGPY_QUERY | typeof CGPY_QUERY | typeof GCPY_QUERY | typeof CCY_QUERY | typeof CCPY2_QUERY | typeof CGPY2_QUERY | typeof GCPY2_QUERY = PLACEHOLDER_QUERY;
  let skipQuery: boolean = false;

  if (view === UIView.Products) {
    if (
      countryId &&
      partnerCountryId === undefined &&
      groupId === undefined &&
      partnerGroupId === undefined &&
      productId === undefined
    ) {
      queryToUse = CPY_QUERY;
    } else if (
      countryId &&
      partnerCountryId &&
      groupId === undefined &&
      partnerGroupId === undefined &&
      productId === undefined
    ) {
      queryToUse = CCPY_QUERY;
    } else if (
      countryId === undefined &&
      partnerCountryId === undefined &&
      groupId &&
      partnerGroupId
    ) {
      queryToUse = GGPY_QUERY;
    } else if (
      countryId &&
      partnerCountryId === undefined &&
      groupId === undefined &&
      partnerGroupId &&
      productId === undefined
    ) {
      queryToUse = CGPY_QUERY;
    } else if (
      countryId === undefined &&
      partnerCountryId &&
      groupId &&
      partnerGroupId === undefined &&
      productId === undefined
    ) {
      queryToUse = GCPY_QUERY;
    } else {
      skipQuery = true;
    }
  } else if (view === UIView.Markets) {
    if (
      countryId &&
      partnerCountryId === undefined &&
      groupId === undefined &&
      partnerGroupId === undefined &&
      productId === undefined
    ) {
      queryToUse = CCY_QUERY;
    } else if (
      countryId &&
      partnerCountryId === undefined &&
      groupId === undefined &&
      partnerGroupId === undefined &&
      productId !== undefined
    ) {
      queryToUse = CCPY2_QUERY;
    } else if (
      countryId === undefined &&
      partnerCountryId === undefined &&
      groupId === undefined &&
      parseFloat(String(partnerGroupId)) === numericWorldGroupId &&
      productId
    ) {
      queryToUse = CGPY2_QUERY;
    } else if (
      countryId === undefined &&
      partnerCountryId === undefined &&
      groupId &&
      partnerGroupId === undefined
    ) {
      queryToUse = GCPY2_QUERY;
    } else {
      skipQuery = true;
    }
  } else {
    skipQuery = true;
  }

  return { queryToUse, queryIsInvalid: skipQuery };
};

export default determineEndpointFacet;
